import type { MessageDescriptor } from 'react-intl';
import {
  PROFILE_DEFAULT_FOCUS,
  profileImageSpec,
  profileSourceNeeds,
  type ArtistProfileSource,
  type ProfileMakeItem,
  type SerializedProfileAsset,
} from 'fym-shared/src/music24/artistProfile';
import { downloadMediaUrl, inlineMediaUrl } from '../inlineMediaUrl';
import type { HeaderPreviewGround } from '../studio/spotifyHeader/FrameGround';
import { profileToolMessages as t } from './tools/profileToolMessages';

/** The address that an `<img>` shows a version with, or null when it has no file. Pure. */
export function profileFileUrl(asset: SerializedProfileAsset | null): string | null {
  if (asset === null || asset.url === null || asset.fileRemoved) {
    return null;
  }
  return inlineMediaUrl(asset.url);
}

/** The download address of a version (the server names the file), or null. Pure. */
export function profileDownloadUrl(asset: SerializedProfileAsset | null): string | null {
  if (asset === null || asset.url === null || asset.fileRemoved) {
    return null;
  }
  return downloadMediaUrl(asset.url);
}

/**
 * The ground of a made file: the file itself. Its aspect is the frame aspect, so the crop is the
 * whole picture and the Free mark is in it already. Null when the version has no file. Pure.
 */
export function profileFileGround(asset: SerializedProfileAsset | null): HeaderPreviewGround | null {
  const url = profileFileUrl(asset);
  return url === null ? null : { kind: 'picture', url, focus: { x: 0.5, y: 0.5 } };
}

/** The name of a source on a card and in the versions list. */
export function sourceLabel(source: ArtistProfileSource, plainCover: boolean): MessageDescriptor {
  switch (source) {
    case 'cover':
      return plainCover ? t.sourceCoverPlain : t.sourceCover;
    case 'photo':
      return t.sourcePhoto;
    case 'bank':
      return t.sourceBank;
    case 'ai':
      return t.sourceAi;
    case 'theme':
      return t.sourceTheme;
    default:
      return t.sourceTheme;
  }
}

/** True when the cover of the kind is drawn as it is (the artist photo, a press photo). Pure. */
export function kindHasPlainCover(asset: Pick<SerializedProfileAsset, 'kind' | 'variant'>): boolean {
  return profileImageSpec(asset.kind, asset.variant).coverMode === 'plain';
}

/**
 * The make item that "Try again" sends for a failed version: the same kind, picture and look.
 * Null when the version misses the id that its source needs, or when the picture is gone: then
 * "Try again" opens the tool. The three press crops share one make. Pure.
 */
export function retryItemOf(asset: SerializedProfileAsset): ProfileMakeItem | null {
  const needs = profileSourceNeeds(asset.source);
  // The picture is gone (SOURCE_GONE): the same make fails again, so the tool opens.
  if (asset.failCode === 'SOURCE_GONE') {
    return null;
  }
  if (needs === 'picture' && asset.pictureUuid === null) {
    return null;
  }
  if (needs === 'file' && asset.fileId === null) {
    return null;
  }
  if (needs === 'release' && asset.catalogItemUuid === null) {
    return null;
  }
  const item: ProfileMakeItem = { kind: asset.kind, source: asset.source };
  if (asset.kind !== 'pressPhoto') {
    item.variant = asset.variant;
  }
  if (asset.catalogItemUuid !== null) {
    item.catalogItemUuid = asset.catalogItemUuid;
  }
  if (needs === 'file' && asset.fileId !== null) {
    item.fileId = asset.fileId;
  }
  if (needs === 'picture' && asset.pictureUuid !== null) {
    item.pictureUuid = asset.pictureUuid;
  }
  if (asset.focusX !== null || asset.focusY !== null) {
    item.focusX = asset.focusX ?? PROFILE_DEFAULT_FOCUS.x;
    item.focusY = asset.focusY ?? PROFILE_DEFAULT_FOCUS.y;
  }
  item.lookFrom = asset.look.from === 'release' && asset.catalogItemUuid !== null ? 'release' : 'artist';
  return item;
}
